import { TrackingEventName, useTrackingStore } from '@finn/ua-tracking';
import { App, cn, parseToHtml, useCurrentApp, useIntl } from '@finn/ui-utils';
import { ReactNode, useEffect } from 'react';

import { useIsFeatureVisible } from '../hooks/useIsFeatureVisible';
import { useValue } from '../hooks/useValue';
import { FormatConfig, VisibilityConfig } from '../types';

export type TextBlockProps = {
  textId?: string;
  text?: string | ReactNode;
  formatConfig?: FormatConfig;
  fontSize?: number;
  backgroundColor?: string;
  padding?: string;
  borderRadius?: string;
  className?: string;
  visibilityConfig?: VisibilityConfig;
  trimmedText?: boolean;
};

// TODO remove me after better solution found for shared features
// likely by conditional cosmic check for b2b
const applyB2BHacks = (text: string) => {
  // Apply specific hacks for B2B text formatting
  return text
    ?.replaceAll(
      'https://support.finn.com/hc/de-de/sections/15966888835217',
      'https://support-b2b.finn.com/hc/de/articles/12809520592785-Kann-das-gebuchte-Kilometerpaket-w%C3%A4hrend-der-Abo-Laufzeit-ver%C3%A4ndert-werden'
    )
    ?.replaceAll(
      'https://support.finn.com/hc/de-de/articles/15966982470545-Wie-ver%C3%A4ndert-sich-meine-monatliche-Abo-Rate-wenn-ich-mein-Kilometerpaket-erh%C3%B6he',
      'https://support-b2b.finn.com/hc/de/articles/12809520592785-Kann-das-gebuchte-Kilometerpaket-w%C3%A4hrend-der-Abo-Laufzeit-ver%C3%A4ndert-werden'
    )
    ?.replaceAll('die in deiner', 'die in Ihrer')
    ?.replaceAll('erscheinen auf deiner', 'erscheinen auf Ihrer');
};

export const TextBlock = ({
  textId,
  text,
  fontSize,
  backgroundColor,
  padding,
  borderRadius,
  formatConfig,
  className,
  visibilityConfig,
  trimmedText,
}: TextBlockProps) => {
  const [formatedText] = useValue(
    typeof text === 'string' ? text : '',
    formatConfig
  );
  const track = useTrackingStore((state) => state.track);

  const intl = useIntl();

  const isVisible = useIsFeatureVisible(visibilityConfig);
  const app = useCurrentApp();

  useEffect(() => {
    if (!isVisible || !textId) {
      return;
    }
    track(TrackingEventName.TEXT_BLOCK_SHOWN, {
      location: textId,
    });
  }, [textId, track, isVisible]);

  const content = textId
    ? intl.formatMessage(textId)
    : typeof text === 'string'
      ? parseToHtml(
          app === App.B2B ? applyB2BHacks(formatedText) : formatedText
        )
      : text;

  if (!isVisible) {
    return null;
  }

  return (
    <p
      style={{ fontSize, backgroundColor, padding, borderRadius }}
      className={cn(
        'body-14-light',
        trimmedText ? '' : 'before:hidden after:hidden',
        '[&_ul]:ps-10 [&_ul_li]:list-[circle]',
        className
      )}
    >
      {content}
    </p>
  );
};
